import { useEffect, useRef } from "react";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import { reportUiError, toUiError } from "../../../errors/uiError";
import { parseDocumentVersionRef } from "../../../shared/references/contentReferences";
import type { WorkflowRole } from "../model/workflowDraft";
import type { PaletteItem } from "./paletteDrag";

/** Resolve the pinned handoff, never replace it with the library's newer version. */
export async function carriedItem(reference: string, selfItemId: string, signal: AbortSignal): Promise<PaletteItem | null> {
  if (parseDocumentVersionRef(reference) === null) return null;
  const item = await readAuthoredItem(reference, signal);
  if (item.itemId === selfItemId || (item.kind !== "step" && item.kind !== "workflow")) return null;
  return { kind: item.kind, reference: item.reference, name: item.name };
}

/** Add the item "Use in a Workflow" carried (`reference`, the edit's `carry`) to the draft once, as the editor opens. Nothing is committed here. */
export function useCarriedItem(reference: string | null, selfItemId: string, add: (item: PaletteItem, role: WorkflowRole) => void, invalid: () => void): void {
  const consumed = useRef<string | null>(null);
  useEffect(() => {
    if (reference === null || consumed.current === reference) return;
    const controller = new AbortController();
    void carriedItem(reference, selfItemId, controller.signal).then(
      (item) => {
        if (controller.signal.aborted) return;
        consumed.current = reference;
        if (item === null) invalid();
        else add(item, "main");
      },
      (caught: unknown) => { if (!controller.signal.aborted) reportUiError(toUiError(caught)); },
    );
    return () => { controller.abort(); };
  }, [reference, selfItemId, add, invalid]);
}
